import { formatDate } from '@angular/common';
import { computed, inject, LOCALE_ID } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ProblemType, SimlabErrorDto } from '@simOn/common/http';
import { defer, exhaustMap, map, of, pipe, switchMap, tap } from 'rxjs';
import {
  AGENT_ACCESS_TEXTS,
  AgentAccessTexts,
  AgentActionDto,
  AgentAssetPayload,
  AgentActionType,
  AgentAssistantState,
  AgentBlockedState,
  AgentCardChange,
  AgentCardPill,
  AgentCardPillTone,
  AgentChatAction,
  AgentChatError,
  AgentChatMessage,
  AgentChatMessageStatus,
  AgentConversationListEntryDto,
  AgentEventPayload,
  AgentFieldChange,
  AgentMessageDto,
  AgentMessageRole,
  AgentTicketPayload,
  AgentTicketStatus,
  AgentUserStatusDto,
  CreateResourceActionBaseDto,
  getAgentAssistantState,
  isCreateAssetAction,
  isCreateEventAction,
  isCreateTicketAction,
  isNavigateToAssetAction,
  isNavigateToEventAction,
  isNavigateToTicketAction,
  isOpenDocumentAction,
  isSupportedAgentAction,
  isUpdateAssetAction,
  isUpdateEventAction,
  isUpdateTicketAction,
  isUpdateTicketStatusAction,
  normalizeActionType
} from '../model';
import { AgentService } from '../service';

/** A send in flight: the backend takes one message at a time, so there is at most one. */
export interface AgentPendingSend {
  /** `null` while the conversation of a first message is still being created. */
  conversationId: string | null;
  message: string;
  /** Local id of the user message appended to the chat. */
  userMessageId: string;
}

/** A send that failed while its chat was not open; shown with Retry once the chat is opened again. */
export interface AgentFailedSend {
  conversationId: string;
  message: string;
  userMessageId: string;
}

export interface AgentChatState {
  conversations: AgentConversationListEntryDto[];
  conversationsLoaded: boolean;
  conversationsLoading: boolean;
  selectedConversationId: string | null;
  messages: AgentChatMessage[];
  messagesLoading: boolean;
  pendingSend: AgentPendingSend | null;
  failedSends: AgentFailedSend[];
  error: AgentChatError | null;
  /** Last `GetUserStatus` response; `null` until it is loaded. */
  userStatus: AgentUserStatusDto | null;
  userStatusLoading: boolean;
  /** When `userStatus` was last fetched (ISO date); `null` until it is loaded. */
  userStatusCheckedAt: string | null;
  /** Derived from `userStatus`, or switched right away by an access / token error; `null` until known. */
  assistantState: AgentAssistantState | null;
  /** Text of a message the backend rejected for access / tokens; handed back to the composer once. */
  restoredMessage: string | null;
}

const initialState: AgentChatState = {
  conversations: [],
  conversationsLoaded: false,
  conversationsLoading: false,
  selectedConversationId: null,
  messages: [],
  messagesLoading: false,
  pendingSend: null,
  failedSends: [],
  error: null,
  userStatus: null,
  userStatusLoading: false,
  userStatusCheckedAt: null,
  assistantState: null,
  restoredMessage: null
};

const ERROR_MESSAGES = {
  unreachable: $localize`:@@AI_AGENT_ERROR_UNREACHABLE:Couldn't reach the assistant`,
  unreachableDetail: $localize`:@@AI_AGENT_ERROR_UNREACHABLE_DETAIL:Check your connection and try again. Your conversation is safe.`,
  conversationNotFound: $localize`:@@AI_AGENT_ERROR_CONVERSATION_NOT_FOUND:Conversation not found.`,
  actionFailed: $localize`:@@AI_AGENT_ERROR_ACTION_FAILED:The action could not be performed. Please try again.`
} as const;

/** Errors after which the message was not processed and the assistant is blocked in the given state. */
const BLOCKING_ERROR_STATES: Partial<Record<AgentChatError['kind'], AgentAssistantState>> = {
  accessNotGranted: 'notEnabled',
  limitReached: 'noTokens'
};

/** Text of the backend `ArgumentException` for a missing / foreign / deleted conversation. */
const CONVERSATION_NOT_FOUND_MESSAGE = 'Conversation not found.';

/** Only user and assistant messages are shown; other roles are tolerated but hidden. */
const VISIBLE_ROLES: readonly AgentMessageRole[] = [AgentMessageRole.User, AgentMessageRole.Assistant];

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const localId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;

/** Swagger declares the role as a number, but the API may serialize enums by name ("User"); both are accepted. */
const normalizeRole = (role: AgentMessageDto['role']): AgentMessageRole => {
  if (typeof role === 'number') return role;
  const name = `${role}`.trim();
  const byName = AgentMessageRole[(name.charAt(0).toUpperCase() + name.slice(1)) as keyof typeof AgentMessageRole];
  return typeof byName === 'number' ? byName : Number(name) || AgentMessageRole.Unknown;
};

/** Labels of the asset payload fields shown in an `UpdateAsset` card; the form labels are reused. */
const ASSET_FIELD_LABELS: Record<keyof AgentAssetPayload, string> = {
  name: $localize`:@@ADD_ASSET_NAME_LABEL:Name`,
  roomId: $localize`:@@ADD_ASSET_ROOM_LABEL:Room`,
  categoryId: $localize`:@@ADD_ASSET_CATEGORY_LABEL:Category`,
  textNote: $localize`:@@ADD_ASSET_DESCRIPTION_LABEL:Description`,
  brand: $localize`:@@ADD_ASSET_BRAND_LABEL:Brand`,
  model: $localize`:@@ADD_ASSET_MODEL_LABEL:Model`,
  serialNumber: $localize`:@@ADD_ASSET_SERIAL_NUMBER_LABEL:Serial Number`,
  purchasePrice: $localize`:@@ADD_ASSET_PURCHASE_PRICE_LABEL:Purchase Price`,
  purchaseCurrency: $localize`:@@ADD_ASSET_CURRENCY_PLACEHOLDER:Currency`,
  currentValue: $localize`:@@ADD_ASSET_CURRENT_VALUE_LABEL:Current Value`,
  currentCurrency: $localize`:@@ADD_ASSET_CURRENCY_PLACEHOLDER:Currency`,
  purchaseDate: $localize`:@@ADD_ASSET_PURCHASE_DATE_LABEL:Purchase Date`,
  warrantyEnd: $localize`:@@ADD_ASSET_WARRANTY_ENDS_LABEL:Warranty Ends`,
  events: $localize`:@@AI_AGENT_FIELD_REMINDERS:Reminders`,
  hyperlinks: $localize`:@@AI_AGENT_FIELD_LINKS:Links`
};

/** Labels of the ticket payload fields shown in the ticket cards; the task form labels are reused. */
const TICKET_FIELD_LABELS: Record<keyof AgentTicketPayload | 'status', string> = {
  name: $localize`:@@TASKS_COL_NAME:Task Name`,
  htmlText: $localize`:@@TASKS_DESCRIPTION:Description`,
  dueDate: $localize`:@@TASKS_COL_DUE_DATE:Due Date`,
  dueTime: $localize`:@@TASKS_DUE_TIME:Due time`,
  roomId: $localize`:@@TASKS_ROOM:Room`,
  assetId: $localize`:@@TASKS_CONNECTED_ASSET:Connected Asset`,
  taskType: $localize`:@@TASKS_TASK_TYPE:Task type`,
  assigneeIds: $localize`:@@TASKS_ASSIGNEE:Assignee`,
  notificationTemplates: $localize`:@@TASKS_NOTIFICATIONS:Notifications`,
  contractorId: $localize`:@@TASKS_CONTRACTOR_LABEL:Contractor`,
  checklist: $localize`:@@TASKS_CONNECTED_CHECKLIST:Connected Checklist`,
  status: $localize`:@@STATUS:Status`
};

/** Labels of the event payload fields shown in the event cards; the event form labels are reused. */
const EVENT_FIELD_LABELS: Record<keyof AgentEventPayload, string> = {
  name: $localize`:@@EVENT_NAME_LABEL:Name`,
  description: $localize`:@@TASKS_DESCRIPTION:Description`,
  startDate: $localize`:@@EVENT_START_DATE:Start date`,
  endDate: $localize`:@@EVENT_END_DATE:End date`,
  isAllDay: $localize`:@@EVENT_ALL_DAY_LABEL:All day(s)`,
  startTime: $localize`:@@EVENT_START_TIME:Start time`,
  endTime: $localize`:@@EVENT_END_TIME:End time`,
  roomId: $localize`:@@TASKS_ROOM:Room`,
  isOpen: $localize`:@@EVENT_OPEN_FOR_ALL:Open for all`,
  assigneeIds: $localize`:@@EVENT_ASSIGNEES_LABEL:Assignee(s)`,
  recurrenceType: $localize`:@@EVENT_FREQUENCY:Frequency`,
  recurrenceDaysOfWeek: $localize`:@@AI_AGENT_FIELD_WEEKDAYS:Weekdays`,
  recurrenceDayOfMonth: $localize`:@@AI_AGENT_FIELD_DAY_OF_MONTH:Day of month`,
  recurrenceWeekOfMonth: $localize`:@@AI_AGENT_FIELD_WEEK_OF_MONTH:Week of month`,
  recurrenceDayOfWeekInTheMonth: $localize`:@@AI_AGENT_FIELD_WEEKDAY:Weekday`,
  recurrenceEndDate: $localize`:@@AI_AGENT_FIELD_RECURRENCE_END:Repeat until`,
  notificationTemplates: $localize`:@@TASKS_NOTIFICATIONS:Notifications`
};

const TICKET_STATUS_LABELS: Record<AgentTicketStatus, string> = {
  Unknown: '',
  Open: $localize`:@@3D_TICKET_MODAL_STATUSES_OPEN:Open`,
  InProgress: $localize`:@@3D_TICKET_MODAL_STATUSES_IN_PROGRESS:In progress`,
  Closed: $localize`:@@3D_TICKET_MODAL_STATUSES_CLOSED:Closed`
};

const TICKET_STATUS_TONES: Record<AgentTicketStatus, AgentCardPillTone> = {
  Unknown: 'neutral',
  Open: 'neutral',
  InProgress: 'warning',
  Closed: 'success'
};

const CARD_TEXTS = {
  asset: $localize`:@@AI_AGENT_CARD_ASSET:Asset`,
  task: $localize`:@@AI_AGENT_CARD_TASK:Task`,
  event: $localize`:@@AI_AGENT_CARD_EVENT:Event`,
  document: $localize`:@@AI_AGENT_CARD_DOCUMENT:Document`,
  allDay: $localize`:@@EVENT_ALL_DAY_LABEL:All day(s)`,
  overdue: $localize`:@@AI_AGENT_OVERDUE:overdue`
} as const;

const due = (date: string): string => $localize`:@@AI_AGENT_DUE:Due ${date}:date:`;

/** What a draft card does; shown in its eyebrow. */
const DRAFT_TYPE_NAMES: Partial<Record<AgentActionType, string>> = {
  [AgentActionType.CreateAsset]: $localize`:@@AI_AGENT_NEW_ASSET:New asset`,
  [AgentActionType.UpdateAsset]: $localize`:@@AI_AGENT_EDIT_ASSET:Edit asset`,
  [AgentActionType.CreateTicket]: $localize`:@@AI_AGENT_NEW_TASK:New task`,
  [AgentActionType.UpdateTicket]: $localize`:@@AI_AGENT_EDIT_TASK:Edit task`,
  [AgentActionType.UpdateTicketStatus]: $localize`:@@AI_AGENT_STATUS_CHANGE:Status change`,
  [AgentActionType.CreateEvent]: $localize`:@@AI_AGENT_NEW_EVENT:New event`,
  [AgentActionType.UpdateEvent]: $localize`:@@AI_AGENT_EDIT_EVENT:Edit event`
};

const SEPARATOR = ' \u00b7 ';

/** Dates come as ISO strings (or default values); anything unparsable is left out of the card. */
const formatCardDate = (value: string | null | undefined, locale: string, withTime = false): string | null => {
  if (!value || value.startsWith('0001-')) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return formatDate(date, withTime ? 'shortDate HH:mm' : 'shortDate', locale);
};

/** "HH:mm:ss" -> "HH:mm" */
const formatCardTime = (value: string | null | undefined): string | null => (value ? value.slice(0, 5) : null);

const joinParts = (...parts: (string | null | undefined)[]): string | null => {
  const text = parts.filter((part): part is string => !!part).join(SEPARATOR);
  return text || null;
};

const toChange = ({ field, oldValue, newValue }: AgentFieldChange, labels: Record<string, string>): AgentCardChange => ({
  label: labels[field] ?? field,
  oldValue,
  newValue
});

const similarNames = (dto: CreateResourceActionBaseDto): string[] =>
  dto.similarResources.map(({ name }) => name).filter((name): name is string => !!name);

/** Local "YYYY-MM-DD" of a date. */
const dayKey = (date: Date): string =>
  `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, '0')}-${`${date.getDate()}`.padStart(2, '0')}`;

/** A ticket is overdue from the day after its due day until it is closed. */
const isOverdue = (dueDate: string, status: AgentTicketStatus): boolean => {
  const dueDay = /^\d{4}-\d{2}-\d{2}/.exec(dueDate)?.[0];
  return status !== 'Closed' && !!dueDay && dueDay < dayKey(new Date());
};

const ticketPills = (dueDate: string | null, status: AgentTicketStatus, locale: string): AgentCardPill[] => {
  const pills: AgentCardPill[] = [];
  const dueText = formatCardDate(dueDate, locale);
  if (dueDate && dueText) {
    const overdue = isOverdue(dueDate, status);
    pills.push({ label: overdue ? `${due(dueText)}${SEPARATOR}${CARD_TEXTS.overdue}` : due(dueText), tone: overdue ? 'danger' : 'neutral' });
  }
  const statusLabel = TICKET_STATUS_LABELS[status];
  if (statusLabel) pills.push({ label: statusLabel, tone: TICKET_STATUS_TONES[status] });
  return pills;
};

/** "05/11/2026 - 07/11/2026 \u00b7 All day(s)" or "05/11/2026 \u00b7 10:00 - 11:00" */
const eventWhen = (
  start: string | null,
  end: string | null,
  startTime: string | null,
  endTime: string | null,
  isAllDay: boolean,
  locale: string
): string | null => {
  const startText = formatCardDate(start, locale);
  const endText = end && end !== start ? formatCardDate(end, locale) : null;
  const dates = startText && endText ? `${startText} - ${endText}` : (startText ?? endText);
  if (isAllDay) return joinParts(dates, CARD_TEXTS.allDay);
  const times = [formatCardTime(startTime), formatCardTime(endTime)].filter(Boolean).join(' - ');
  return joinParts(dates, times || null);
};

type CardTexts = Pick<AgentChatAction, 'kind' | 'label' | 'helper' | 'pills' | 'changes' | 'duplicates'>;

const card = (texts: Partial<CardTexts> & Pick<CardTexts, 'kind' | 'label'>): CardTexts => ({
  helper: null,
  pills: [],
  changes: [],
  duplicates: [],
  ...texts
});

const actionTexts = (dto: AgentActionDto, locale: string): CardTexts => {
  if (isNavigateToAssetAction(dto)) {
    return card({ kind: 'navigation', label: dto.targetName ?? '', helper: joinParts(CARD_TEXTS.asset, dto.roomName) });
  }
  if (isNavigateToTicketAction(dto)) {
    return card({
      kind: 'navigation',
      label: dto.targetName ?? '',
      helper: joinParts(CARD_TEXTS.task, dto.roomName),
      pills: ticketPills(dto.dueDate, dto.status, locale)
    });
  }
  if (isNavigateToEventAction(dto)) {
    const when = eventWhen(dto.startDate, dto.endDate, dto.startTime, dto.endTime, dto.isAllDay, locale);
    return card({ kind: 'navigation', label: dto.targetName ?? '', helper: joinParts(CARD_TEXTS.event, when, dto.roomName) });
  }
  if (isOpenDocumentAction(dto)) return card({ kind: 'navigation', label: dto.fileName ?? '', helper: CARD_TEXTS.document });
  if (isCreateAssetAction(dto)) {
    return card({
      kind: 'draft',
      label: dto.payload.name,
      helper: joinParts(dto.categoryName, dto.roomName),
      duplicates: similarNames(dto)
    });
  }
  if (isUpdateAssetAction(dto)) {
    return card({
      kind: 'draft',
      label: dto.targetName ?? '',
      changes: dto.changes.map((change) => toChange(change, ASSET_FIELD_LABELS))
    });
  }
  if (isCreateTicketAction(dto)) {
    const { payload } = dto;
    const dueText = [formatCardDate(payload.dueDate, locale), formatCardTime(payload.dueTime)].filter(Boolean).join(' ');
    const assignee = dto.assignees[0]?.name;
    return card({
      kind: 'draft',
      label: payload.name ?? '',
      helper: joinParts(
        dueText ? due(dueText) : null,
        assignee ? `${TICKET_FIELD_LABELS.assigneeIds}: ${assignee}` : null,
        dto.roomName
      ),
      duplicates: similarNames(dto)
    });
  }
  if (isUpdateTicketAction(dto)) {
    return card({
      kind: 'draft',
      label: dto.targetName ?? '',
      changes: dto.changes.map((change) => toChange(change, TICKET_FIELD_LABELS))
    });
  }
  if (isUpdateTicketStatusAction(dto)) {
    const statusText = (value: string | null | undefined): string | null =>
      value ? TICKET_STATUS_LABELS[value as AgentTicketStatus] || value : null;
    const previous = statusText(dto.changes.find(({ field }) => field === 'status')?.oldValue);
    const next = statusText(dto.payload.status);
    return card({ kind: 'status', label: dto.targetName ?? '', helper: previous ? `${previous} \u2192 ${next}` : next });
  }
  if (isCreateEventAction(dto)) {
    const { payload } = dto;
    const when = eventWhen(
      payload.startDate,
      payload.endDate,
      payload.startTime,
      payload.endTime,
      payload.isAllDay ?? false,
      locale
    );
    return card({
      kind: 'draft',
      label: payload.name ?? '',
      helper: joinParts(when, dto.roomName),
      duplicates: similarNames(dto)
    });
  }
  if (isUpdateEventAction(dto)) {
    return card({
      kind: 'draft',
      label: dto.targetName ?? '',
      changes: dto.changes.map((change) => toChange(change, EVENT_FIELD_LABELS))
    });
  }
  return card({ kind: 'navigation', label: '' });
};

/** Unsupported action types are dropped so the chat never shows a card it cannot honour. */
const toChatActions = (actions: AgentMessageDto['actions'], locale: string): AgentChatAction[] =>
  (actions ?? []).filter(isSupportedAgentAction).map((dto) => {
    const type = normalizeActionType(dto.type);
    return {
      id: dto.id || localId(),
      type,
      typeName: DRAFT_TYPE_NAMES[type] ?? null,
      ...actionTexts(dto, locale),
      canGoToPosition: isNavigateToAssetAction(dto) && dto.hasPosition && !!dto.transformation,
      completed: false,
      completedAt: null,
      dto
    };
  });

/** Flags the card as done (its draft was saved) so it cannot be submitted again. */
const markActionCompleted = (messages: AgentChatMessage[], actionId: string): AgentChatMessage[] => {
  const completedAt = new Date().toISOString();
  return messages.map((message) =>
    message.actions.some(({ id }) => id === actionId)
      ? {
          ...message,
          actions: message.actions.map((action) =>
            action.id === actionId ? { ...action, completed: true, completedAt } : action
          )
        }
      : message
  );
};

/** History messages come with default ids and dates; they get local ids so the list can track them. */
const toChatMessage = (dto: AgentMessageDto, locale: string): AgentChatMessage => ({
  id: dto.id && dto.id !== EMPTY_GUID ? dto.id : localId(),
  role: normalizeRole(dto.role),
  content: dto.content ?? '',
  actions: toChatActions(dto.actions, locale),
  createdAt: dto.createdAt?.startsWith('0001-') ? null : (dto.createdAt ?? null),
  usage: dto.usage ?? null,
  status: 'sent'
});

/** The interceptors rethrow either the `HttpErrorResponse` or its body; both shapes are tolerated. */
const toChatError = (err: unknown): AgentChatError => {
  const body = ((err as { error?: unknown })?.error ?? err) as Partial<SimlabErrorDto> | undefined;
  if (body?.ErrorCode === ProblemType.AiAccessNotGranted) {
    return { kind: 'accessNotGranted', message: AGENT_ACCESS_TEXTS.notEnabled.description };
  }
  if (body?.ErrorCode === ProblemType.AiUsageLimitReached) {
    return { kind: 'limitReached', message: AGENT_ACCESS_TEXTS.noTokens.description };
  }
  if (body?.Messages?.includes(CONVERSATION_NOT_FOUND_MESSAGE)) {
    return { kind: 'conversationNotFound', message: ERROR_MESSAGES.conversationNotFound };
  }
  return { kind: 'generic', message: ERROR_MESSAGES.unreachable, detail: ERROR_MESSAGES.unreachableDetail };
};

export const AgentChatStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    selectedConversation: computed<AgentConversationListEntryDto | null>(
      () => store.conversations().find((conversation) => conversation.id === store.selectedConversationId()) ?? null
    ),
    /** Title of the selected conversation (generated by the backend from the first message); `null` on a draft. */
    chatTitle: computed<string | null>(() => {
      const selectedId = store.selectedConversationId();
      return selectedId ? (store.conversations().find(({ id }) => id === selectedId)?.title ?? null) : null;
    }),
    hasMessages: computed<boolean>(() => store.messages().length > 0),
    /** A message is on its way (in any chat); the backend takes one at a time. */
    sending: computed<boolean>(() => store.pendingSend() !== null),
    /** Chat the pending send belongs to; `null` when nothing is pending (or its conversation is being created). */
    sendingConversationId: computed<string | null>(() => store.pendingSend()?.conversationId ?? null),
    /** The pending send belongs to the selected chat: its text stays in the input and the reply is awaited here. */
    sendingInSelected: computed<boolean>(() => {
      const pending = store.pendingSend();
      return pending !== null && pending.conversationId === store.selectedConversationId();
    }),
    /** New conversations and messages are allowed only when enabled with tokens left; reading always is. */
    canUseAssistant: computed<boolean>(() => store.assistantState() === 'ready'),
    /** The state blocking the assistant; `null` when it can be used (or the state is not known yet). */
    blockedState: computed<AgentBlockedState | null>(() => {
      const state = store.assistantState();
      return state && state !== 'ready' ? state : null;
    })
  })),
  withComputed((store) => ({
    /** Texts of the blocked state shown in the panel; `null` when the assistant can be used. */
    accessTexts: computed<AgentAccessTexts | null>(() => {
      const state = store.blockedState();
      return state ? AGENT_ACCESS_TEXTS[state] : null;
    }),
    /** Text locked in the input of the selected chat while its message is on its way; `null` otherwise. */
    pendingMessage: computed<string | null>(() => (store.sendingInSelected() ? (store.pendingSend()?.message ?? null) : null)),
    /** Sending waits for the pending reply (in any chat) and for a usable assistant. */
    canSend: computed<boolean>(() => !store.sending() && !store.messagesLoading() && store.canUseAssistant())
  })),
  withMethods((store, agent = inject(AgentService), locale = inject(LOCALE_ID)) => {
    const upsertConversation = (conversation: AgentConversationListEntryDto): void =>
      patchState(store, {
        conversations: [conversation, ...store.conversations().filter(({ id }) => id !== conversation.id)]
      });

    const patchMessage = (id: string, patch: Partial<AgentChatMessage>): void =>
      patchState(store, {
        messages: store.messages().map((message) => (message.id === id ? { ...message, ...patch } : message))
      });

    const userMessageOf = (message: string, id: string, status: AgentChatMessageStatus): AgentChatMessage => ({
      id,
      role: AgentMessageRole.User,
      content: message,
      actions: [],
      createdAt: new Date().toISOString(),
      usage: null,
      status
    });

    /**
     * Messages of a chat that was sent to while it was not open: the pending one (the backend may not list it
     * yet) and the failed ones, appended after the history unless the history already ends with that text.
     */
    const localTail = (conversationId: string, history: AgentChatMessage[]): AgentChatMessage[] => {
      const last = history.at(-1);
      const alreadyListed = (message: string): boolean => last?.role === AgentMessageRole.User && last.content === message;
      const failed = store
        .failedSends()
        .filter((send) => send.conversationId === conversationId)
        .map((send) => userMessageOf(send.message, send.userMessageId, 'failed'));
      const pending = store.pendingSend();
      const pendingTail =
        pending?.conversationId === conversationId && !alreadyListed(pending.message)
          ? [userMessageOf(pending.message, pending.userMessageId, 'pending')]
          : [];
      return [...failed, ...pendingTail];
    };

    const dropConversation = (conversationId: string | null): void =>
      patchState(store, {
        selectedConversationId: null,
        conversations: store.conversations().filter(({ id }) => id !== conversationId)
      });

    const loadConversations = rxMethod<void>(
      pipe(
        tap(() => patchState(store, { conversationsLoading: true })),
        switchMap(() =>
          agent.getConversations().pipe(
            tapResponse({
              next: (conversations) =>
                patchState(store, {
                  conversations: conversations ?? [],
                  conversationsLoaded: true,
                  conversationsLoading: false
                }),
              error: (err) => {
                patchState(store, { conversationsLoading: false, error: toChatError(err) });
                console.error(err);
              }
            })
          )
        )
      )
    );

    /** No polling: fetched on opening the assistant, after every reply and after access / token errors. */
    const loadUserStatus = rxMethod<void>(
      pipe(
        tap(() => patchState(store, { userStatusLoading: true })),
        switchMap(() =>
          agent.getUserStatus().pipe(
            tapResponse({
              next: (userStatus) =>
                patchState(store, {
                  userStatus,
                  userStatusLoading: false,
                  userStatusCheckedAt: new Date().toISOString(),
                  assistantState: getAgentAssistantState(userStatus)
                }),
              error: (err) => {
                // The backend enforces access on every write, so an unknown status does not block the assistant.
                patchState(store, { userStatusLoading: false, assistantState: store.assistantState() ?? 'ready' });
                console.error(err);
              }
            })
          )
        )
      )
    );

    const sendMessage = rxMethod<string>(
      pipe(
        exhaustMap((rawMessage) => {
          const message = rawMessage.trim();
          if (!message || !store.canSend()) return of(null);

          const userMessage = userMessageOf(message, localId(), 'pending');
          patchState(store, {
            messages: [...store.messages(), userMessage],
            pendingSend: { conversationId: store.selectedConversationId(), message, userMessageId: userMessage.id },
            error: null
          });

          const conversationId$ = defer(() => {
            const selectedId = store.selectedConversationId();
            if (selectedId) return of(selectedId);
            return agent.createConversation().pipe(
              tap((conversation) => {
                upsertConversation(conversation);
                patchState(store, {
                  selectedConversationId: conversation.id,
                  pendingSend: { conversationId: conversation.id, message, userMessageId: userMessage.id }
                });
              }),
              map((conversation) => conversation.id)
            );
          });

          return conversationId$.pipe(
            switchMap((conversationId) =>
              agent.sendMessage(conversationId, message).pipe(map((reply) => ({ conversationId, reply })))
            ),
            tapResponse({
              next: ({ conversationId, reply }) => {
                // The user may have opened another chat meanwhile; the reply then stays in its own conversation
                // (it is loaded with the history when that chat is opened again).
                if (conversationId === store.selectedConversationId()) {
                  patchMessage(userMessage.id, { status: 'sent' });
                  patchState(store, { messages: [...store.messages(), toChatMessage(reply, locale)] });
                }
                patchState(store, { pendingSend: null });
                // The backend may have generated a title from the first message; pick it up.
                loadConversations();
                // Every message uses tokens; the remaining amount is read again rather than computed from `usage`.
                loadUserStatus();
              },
              error: (err) => {
                const error = toChatError(err);
                const blockedState = BLOCKING_ERROR_STATES[error.kind];
                if (blockedState) {
                  // The message was not processed: it leaves the chat and goes back to the input.
                  patchState(store, {
                    messages: store.messages().filter(({ id }) => id !== userMessage.id),
                    pendingSend: null,
                    assistantState: blockedState,
                    restoredMessage: message
                  });
                  loadUserStatus();
                  return;
                }
                const conversationId = store.pendingSend()?.conversationId ?? null;
                const inSelected = conversationId === store.selectedConversationId();
                if (inSelected) patchMessage(userMessage.id, { status: 'failed' });
                patchState(store, {
                  pendingSend: null,
                  error,
                  // Not open any more: kept so the chat shows it with Retry when opened again.
                  failedSends:
                    !inSelected && conversationId
                      ? [...store.failedSends(), { conversationId, message, userMessageId: userMessage.id }]
                      : store.failedSends()
                });
                if (error.kind === 'conversationNotFound') dropConversation(store.selectedConversationId());
                console.error(err);
              }
            })
          );
        })
      )
    );

    return {
      loadConversations,
      loadUserStatus,
      /**
       * Appends the user message locally, creates the conversation when none is selected and appends the
       * assistant reply once `SendMessage` resolves. When the backend rejects it for access or tokens, the message
       * is removed from the chat, handed back to the input (`restoredMessage`) and the assistant state switches. Concurrent sends are ignored (`exhaustMap`) because the
       * backend allows a single active message per user. The send stays tied to its chat (`pendingSend`): opening
       * another chat meanwhile shows nothing of it there, and coming back shows it again.
       */
      sendMessage,
      selectConversation: rxMethod<string>(
        pipe(
          tap((conversationId) =>
            patchState(store, {
              selectedConversationId: conversationId,
              messages: [],
              messagesLoading: true,
              error: null
            })
          ),
          switchMap((conversationId) =>
            agent.getConversation(conversationId).pipe(
              tapResponse({
                next: (conversation) => {
                  const history = (conversation.messages ?? [])
                    .map((message) => toChatMessage(message, locale))
                    .filter((message) => VISIBLE_ROLES.includes(message.role));
                  patchState(store, {
                    messages: [...history, ...localTail(conversationId, history)],
                    messagesLoading: false
                  });
                },
                error: (err) => {
                  const error = toChatError(err);
                  patchState(store, { messagesLoading: false, error });
                  if (error.kind === 'conversationNotFound') dropConversation(conversationId);
                  console.error(err);
                }
              })
            )
          )
        )
      ),
      /** Starts an empty draft; the conversation is created on the backend together with the first message. */
      startNewConversation: (): void =>
        patchState(store, { selectedConversationId: null, messages: [], messagesLoading: false, error: null }),
      /** Re-sends a failed message: the failed entry is removed and appended again as pending. */
      retryMessage: (messageId: string): void => {
        const failed = store.messages().find((message) => message.id === messageId && message.status === 'failed');
        if (!failed) return;
        patchState(store, {
          messages: store.messages().filter((message) => message.id !== messageId),
          failedSends: store.failedSends().filter((send) => send.userMessageId !== messageId)
        });
        sendMessage(failed.content);
      },
      /**
       * Performs an action the user picked under an assistant message; a saved draft marks its card as done,
       * failures surface as a chat error.
       */
      executeAction: (action: AgentChatAction): void => {
        patchState(store, { error: null });
        agent
          .handleAction(action.dto)
          .then((outcome) => {
            if (outcome === 'saved') patchState(store, { messages: markActionCompleted(store.messages(), action.id) });
          })
          .catch((err: unknown) => {
            patchState(store, { error: { kind: 'generic', message: ERROR_MESSAGES.actionFailed } });
            console.error(err);
          });
      },
      /** Moves the 3D walk to the position of the action's target; failures surface as a chat error. */
      goToPosition: (action: AgentChatAction): void => {
        patchState(store, { error: null });
        agent.goToPosition(action.dto).catch((err: unknown) => {
          patchState(store, { error: { kind: 'generic', message: ERROR_MESSAGES.actionFailed } });
          console.error(err);
        });
      },
      clearError: (): void => patchState(store, { error: null }),
      /** Called by the composer once it has put the rejected message back into the input. */
      clearRestoredMessage: (): void => patchState(store, { restoredMessage: null }),
      reset: (): void => patchState(store, initialState)
    };
  })
);
